import type { IPDQueueEntry } from '@/types/ipd';

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function formatStickerDateTime(date: Date): string {
  // Format like: 12/03/2026 09:22:00 (matches sample screenshot style)
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()} ${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
}

function formatSex(gender: string | undefined): string {
  const g = String(gender ?? '').toUpperCase();
  if (g === 'MALE') return 'Male';
  if (g === 'FEMALE') return 'Female';
  if (g) return g;
  return '—';
}

function formatAgeYearsMonths(dob: string | undefined): string {
  if (!dob) return '—';
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return '—';
  const today = new Date();

  let years = today.getFullYear() - birth.getFullYear();
  let months = today.getMonth() - birth.getMonth();
  const days = today.getDate() - birth.getDate();

  if (days < 0) months -= 1;
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (years < 0) return '—';

  return `${years} Years ${months} Month`;
}

export function buildIPDStickerHtml(entry: IPDQueueEntry, copies: number): string {
  const ipdNo = entry.ipdNumber ?? '—';
  const uhid = entry.patient?.uhid ?? '—';
  const name = entry.patient?.name ?? '—';
  const ageSex = `${formatAgeYearsMonths(entry.patient?.dob)} / ${formatSex(entry.patient?.gender)}`;
  const consultant = entry.admission?.assignedDoctor?.name ?? '—';
  const doa = entry.admission?.admissionDate
    ? formatStickerDateTime(new Date(entry.admission.admissionDate))
    : formatStickerDateTime(new Date(entry.createdAt));

  const stickers = Array.from({ length: Math.max(1, copies) }, (_, i) => {
    const key = `${ipdNo}-${uhid}-${i}`;
    return `
      <div class="sticker" data-key="${key}">
        <div class="row top">
          <div class="label">I.P.No.</div>
          <div class="value">${ipdNo}</div>
          <div class="label right">UHID</div>
          <div class="value right">${uhid}</div>
        </div>

        <div class="row">
          <div class="label">Name</div>
          <div class="value span-3">${name}</div>
        </div>

        <div class="row">
          <div class="label">Age/Sex</div>
          <div class="value span-3">${ageSex}</div>
        </div>

        <div class="row">
          <div class="label">consultant</div>
          <div class="value span-3">${consultant}</div>
        </div>

        <div class="row">
          <div class="label">D.O.A.</div>
          <div class="value span-3">${doa}</div>
        </div>
      </div>
    `;
  }).join('');

  return `<!DOCTYPE html>
  <html>
  <head>
    <meta charset="utf-8" />
    <title>IPD Stickers</title>
    <style>
      @page { size: A4; margin: 8mm 8mm; }
      * { box-sizing: border-box; }
      body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #000; }

      /* 2 columns, compact layout to fit 24 stickers per A4 */
      .sheet {
        display: grid;
        grid-template-columns: 1fr 1fr;
        column-gap: 14mm;
        row-gap: 6mm;
        padding: 0;
      }

      .sticker {
        width: 100%;
        font-size: 10px;
        line-height: 1.2;
      }

      .row {
        display: grid;
        grid-template-columns: auto auto auto auto;
        column-gap: 4px;
        align-items: baseline;
        margin: 1px 0;
      }

      .row.top { margin-top: 0; }

      .label { font-weight: 600; white-space: nowrap; }
      .value { font-weight: 600; }
      .right { text-align: left; }
      .span-3 { grid-column: 2 / 5; }

      /* make it print-crisp */
      @media print {
        .sticker { break-inside: avoid; page-break-inside: avoid; }
      }
    </style>
  </head>
  <body>
    <div class="sheet">
      ${stickers}
    </div>
    <script>
      window.onload = function() { window.print(); window.onafterprint = function() { window.close(); }; };
    </script>
  </body>
  </html>`;
}

export function printIPDStickers(entry: IPDQueueEntry, copies = 24) {
  const html = buildIPDStickerHtml(entry, copies);
  const w = window.open('', '_blank');
  if (!w) {
    alert('Please allow pop-ups to print stickers.');
    return;
  }
  w.document.write(html);
  w.document.close();
}

